import { ref } from 'vue';
import { TContractor, TContractorFilterResults, TContractorSortingResults } from '@typedefs/contractors';
import { useUserStore } from '../stores/user-store';
import { get, httpDelete, IHTTPMethodOptions, post, put } from './utils';
import api from './api';
import { TAPIResponse } from '@typedefs/api';

const newContractorId = ref('');
const editedContractorRet = ref(null);
const deleteContractorSuccess = ref(false);

const addContractorError = ref('');
const editContractorError = ref('');
const deleteContractorError = ref('');

export const addNewContractorWithUser = async (newContractor: TContractor) => {
  const userStore = useUserStore();
  if (userStore.isLoggedIn) {
    const requestBody = {
      UEN_Contractor: newContractor.UEN_Contractor,
      CompanyName: newContractor.CompanyName,
      Email: newContractor.Email,
      Phone: newContractor.Phone,
      Address: newContractor.Address,
      UEN_TC: newContractor.UEN_TC,
      ServiceType: newContractor.ServiceType,
    };

    try {
      const data = await post(api.contractors.addContractorWithUser(), requestBody, {
        token: userStore.accessToken!,
      });

      if (data.results && typeof data.results === 'object') {
        newContractorId.value = await data.results.TCUserUID;
      }
    } catch (error) {
      addContractorError.value = (error as Error).message;
    }
  }

  return {
    newContractorId,
    addContractorError,
  };
};

export const editContractor = async (contractor_id: string, editedContractor: TContractor) => {
  const fetchData = async () => {
    const userStore = useUserStore();
    if (userStore.isLoggedIn) {
      const requestBody = {
        UEN_Contractor: editedContractor.UEN_Contractor,
        CompanyName: editedContractor.CompanyName,
        Email: editedContractor.Email,
        Phone: editedContractor.Phone,
        Address: editedContractor.Address,
        UEN_TC: editedContractor.UEN_TC,
        ServiceType: editedContractor.ServiceType,
      };

      try {
        const data = await put(api.contractors.editContractor(contractor_id), requestBody, {
          token: userStore.accessToken!,
        });

        if (data.results && typeof data.results === 'object' && !data.results.CodeError) {
          editedContractorRet.value = await data.results;
        }
      } catch (error) {
        editContractorError.value = (error as Error).message;
      }
    }
  };

  await fetchData();

  return {
    editedContractorRet,
    editContractorError,
  };
};

export const deleteContractor = async (contractor_id: string) => {
  const fetchData = async () => {
    const userStore = useUserStore();
    if (userStore.isLoggedIn) {
      try {
        await httpDelete(api.contractors.deleteContractor(contractor_id), {
          token: userStore.accessToken!,
        });
        deleteContractorSuccess.value = true;
      } catch (error) {
        deleteContractorError.value = (error as Error).message;
      }
    }
  };

  await fetchData();

  return { deleteContractorSuccess, deleteContractorError };
};

export const getContractors = async (
  params: string,
  token: string,
): Promise<TAPIResponse<TContractor[], TContractorFilterResults, TContractorSortingResults>> => {
  return get(api.contractors.getAll(params), { token });
};

export const getContractor = async (id: string, token: string, signal: AbortSignal) => {
  return get<{ results: TContractor }>(api.contractors.getSingle(id), { token, signal });
};

export const getContractorFromUEN = async (
  uid: string,
  token: string,
  signal?: AbortSignal,
): Promise<TAPIResponse<TContractor[], TContractorFilterResults, TContractorSortingResults>> => {
  const options: IHTTPMethodOptions = {
    token,
  };

  if (signal) options.signal = signal;

  return get(api.contractors.getAll(`uen_contractor=${uid}`), options);
};
